import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API, authHeaders, useAdminAuthError } from "@/lib/adminApi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import KpiCard from "@/components/admin/KpiCard";
import PageHeader from "@/components/admin/PageHeader";
import AgePyramid from "@/components/admin/AgePyramid";
import FranceMap from "@/components/admin/FranceMap";
import CapitalProjection from "@/components/admin/CapitalProjection";

const pct = (v) => (v == null ? "—" : `${(v * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`);
const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR")} €`);

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { year } = useOutletContext();
  const handleAuthError = useAdminAuthError();

  // Retour d'onboarding Stripe — le return_url Stripe pointe sur /app/dashboard
  useEffect(() => {
    if (searchParams.get("stripe_return") || searchParams.get("stripe_refresh")) {
      navigate("/app/stripe-onboarding?step=return", { replace: true });
    }
  }, [searchParams, navigate]);

  const [summary, setSummary] = useState(null);
  const [actuarial, setActuarial] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, actuarialRes] = await Promise.all([
        axios.get(`${API}/members/analytics/summary?year=${year}`, { headers: authHeaders() }),
        axios.get(`${API}/analytics/actuarial?year=${year}`, { headers: authHeaders() }),
      ]);
      setSummary(summaryRes.data);
      setActuarial(actuarialRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, [year, handleAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Projection actuarielle — fetch séparé, indépendant de l'exercice sélectionné
  // (la population projetée n'est pas filtrée par année, cf. analytics.py) et
  // débouncé pour ne pas appeler l'API à chaque frappe dans le panneau d'hypothèses.
  const [assumptionOverrides, setAssumptionOverrides] = useState({});
  const [projDefaults, setProjDefaults] = useState(null);
  const [projection, setProjection] = useState(null);
  const [projLoading, setProjLoading] = useState(true);
  const debouncedOverrides = useDebouncedValue(assumptionOverrides, 500);
  const projRequestSeq = useRef(0);

  const fetchProjection = useCallback(async (overrides) => {
    setProjLoading(true);
    const seq = ++projRequestSeq.current;
    try {
      const params = new URLSearchParams();
      Object.entries(overrides).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") params.set(key, value);
      });
      const res = await axios.get(`${API}/analytics/projection?${params.toString()}`, { headers: authHeaders() });
      if (seq !== projRequestSeq.current) return; // réponse obsolète (une saisie plus récente est partie derrière)
      setProjection(res.data);
      setProjDefaults((prev) => prev ?? res.data.defaults);
    } catch (err) {
      if (seq !== projRequestSeq.current) return;
      if (!handleAuthError(err)) toast.error("Erreur lors du calcul de la projection.");
    } finally {
      if (seq === projRequestSeq.current) setProjLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => { fetchProjection(debouncedOverrides); }, [debouncedOverrides, fetchProjection]);

  const fund = actuarial?.fund;
  const pop = actuarial?.population;
  const risk = actuarial?.risk;
  const dq = actuarial?.data_quality;

  const paidRate = summary
    ? Math.round((summary.total_paid / Math.max(summary.total_members, 1)) * 100)
    : 0;

  const collectionPct = fund?.collection_rate != null ? Math.min(fund.collection_rate * 100, 100) : null;

  const growthDisplay = fund?.member_growth_yoy == null
    ? "—"
    : `${fund.member_growth_yoy >= 0 ? "+" : ""}${(fund.member_growth_yoy * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;

  const missingBirthDates = pop && dq ? pop.members_total - dq.with_birth_date : 0;
  const costConfigured = risk?.avg_repatriation_cost != null;

  // Prime pure : ambre si elle dépasse la cotisation réelle
  const premiumAccent =
    risk?.pure_premium != null && risk?.actual_cotisation != null && risk.pure_premium > risk.actual_cotisation
      ? "#D97706"
      : undefined;

  const projSummary = projection?.summary;
  const projAssumptions = projection?.assumptions;
  const projHorizon = projAssumptions?.horizon_years ?? 30;

  const equilibriumAccent =
    projSummary?.equilibrium_cotisation != null && risk?.actual_cotisation != null
      ? (projSummary.equilibrium_cotisation > risk.actual_cotisation ? "#DC2626" : "#059669")
      : undefined;

  const breachDisplay = projSummary?.breach_year == null ? "Aucune" : String(projSummary.breach_year);
  const breachAccent = projSummary?.breach_year == null
    ? "#059669"
    : (projSummary.breach_year - year <= 10 ? "#DC2626" : "#D97706");

  const horizonRatio = projSummary?.health_ratio_at_horizon;
  const horizonRatioDisplay = horizonRatio == null ? "—" : `${Math.round(horizonRatio * 100)} %`;
  const horizonRatioAccent = horizonRatio == null
    ? undefined
    : horizonRatio < 1 ? "#DC2626" : horizonRatio < 1.5 ? "#D97706" : "#059669";

  return (
    <div className="space-y-10">

      {/* ── Santé du fonds ── */}
      <section className="fade-up fade-up-1">
        <PageHeader title="Santé du fonds" subtitle={`Exercice ${year}`} rule />
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-px"
          style={{ backgroundColor: "#1F2937", borderRadius: "16px", overflow: "hidden" }}
        >
          <KpiCard
            label="Solde du fonds"
            value={eur(fund?.balance)}
            sub="cotisations + dons − sinistres"
            accent="#A78BFA"
            loading={loading}
          />
          <KpiCard
            label="Taux de collecte"
            value={pct(fund?.collection_rate)}
            sub={
              dq?.members_partial
                ? `${eur(fund?.received_year)} / ${eur(fund?.expected_year)} · ${dq.members_partial} partielle${dq.members_partial > 1 ? "s" : ""}`
                : `${eur(fund?.received_year)} / ${eur(fund?.expected_year)}`
            }
            accent="#60A5FA"
            loading={loading}
          />
          <KpiCard
            label="Nouvelles adhésions"
            value={growthDisplay}
            sub={`vs ${year - 1}`}
            accent={fund?.member_growth_yoy >= 0 ? "#059669" : "#DC2626"}
            loading={loading}
          />
          <KpiCard
            label="À jour"
            value={summary?.total_paid ?? 0}
            sub={`${paidRate}% des ${summary?.total_members ?? 0} membres`}
            accent="#059669"
            loading={loading}
          />
        </div>
        {collectionPct != null && (
          <div className="mt-3 px-1">
            <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${collectionPct}%`, backgroundColor: "#60A5FA" }}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Cotisations {year} : {eur(fund?.received_year)} reçues sur {eur(fund?.expected_year)} attendues
            </p>
          </div>
        )}
      </section>

      {/* ── Population ── */}
      <section className="fade-up fade-up-2">
        <PageHeader title="Population" subtitle={`${pop?.members_total ?? "…"} membres`} rule />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            variant="light"
            label="Âge moyen"
            value={pop?.mean_age != null ? `${pop.mean_age} ans` : "—"}
            sub={pop?.median_age != null ? `médiane ${pop.median_age} ans` : null}
            loading={loading}
          />
          <KpiCard
            variant="light"
            label="Plus de 60 ans"
            value={pct(pop?.pct_over_60)}
            sub="de la population"
            accent={pop?.pct_over_60 > 0.25 ? "#D97706" : undefined}
            loading={loading}
          />
          <KpiCard
            variant="light"
            label="Hommes / Femmes"
            value={pop ? `${pop.gender_split.M} / ${pop.gender_split.F}` : "—"}
            sub={pop?.gender_split.unknown ? `${pop.gender_split.unknown} non renseigné${pop.gender_split.unknown > 1 ? "s" : ""}` : null}
            loading={loading}
          />
          <KpiCard
            variant="light"
            label="Enfants couverts"
            value={pop?.children_total ?? "—"}
            sub="déclarés par les membres"
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AgePyramid ageBands={pop?.age_bands} loading={loading} />
          <FranceMap postalDistribution={pop?.postal_distribution} loading={loading} />
        </div>
      </section>

      {/* ── Risque actuariel ── */}
      <section className="fade-up fade-up-3">
        <PageHeader
          title="Risque actuariel"
          subtitle="Estimations fondées sur la structure d'âge de la population"
          rule
        />

        {!costConfigured && !loading && (
          <div className="mb-4 rounded-2xl bg-amber-50 border border-amber-100 px-5 py-4 flex items-center justify-between gap-4">
            <p className="text-sm text-amber-800">
              Configurez le <strong>coût moyen de rapatriement</strong> pour activer les indicateurs de
              couverture du fonds.
            </p>
            <Link
              to="/app/parametres"
              className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
            >
              Configurer →
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            variant="light"
            label="Prime pure / membre"
            value={risk?.pure_premium != null ? eur(risk.pure_premium) : "—"}
            sub={risk?.actual_cotisation != null ? `cotisation actuelle : ${eur(risk.actual_cotisation)}` : null}
            accent={premiumAccent}
            loading={loading}
          />
          <KpiCard
            variant="light"
            label="Cotisation d'équilibre"
            value={projSummary?.equilibrium_cotisation != null ? eur(projSummary.equilibrium_cotisation) : "—"}
            sub={risk?.actual_cotisation != null ? `cotisation actuelle : ${eur(risk.actual_cotisation)}` : null}
            accent={equilibriumAccent}
            loading={projLoading}
          />
          <KpiCard
            variant="light"
            label="Année de rupture"
            value={breachDisplay}
            sub={projSummary?.breach_year == null ? `aucune sur ${projHorizon} ans` : "solde sous le besoin en capital"}
            accent={breachAccent}
            loading={projLoading}
          />
          <KpiCard
            variant="light"
            label={`Couverture à ${projHorizon} ans`}
            value={horizonRatioDisplay}
            sub="fonds projeté ÷ besoin en capital"
            accent={horizonRatioAccent}
            loading={projLoading}
          />
        </div>

        <div className="mt-4 mb-6 space-y-1 px-1">
          {missingBirthDates > 0 && (
            <p className="text-xs text-slate-400">
              {missingBirthDates} membre{missingBirthDates > 1 ? "s" : ""} sans date de naissance —
              estimation moyenne appliquée.
            </p>
          )}
          <p className="text-xs text-slate-400">
            Table de mortalité simplifiée (approximation INSEE France 2019-2021), tranches de 5 ans par sexe.
          </p>
        </div>

        <CapitalProjection
          loading={projLoading}
          series={projection?.series}
          summary={projSummary}
          dataQuality={projection?.data_quality}
          assumptions={projAssumptions}
          defaults={projDefaults}
          overrides={assumptionOverrides}
          onAssumptionsChange={setAssumptionOverrides}
        />
      </section>
    </div>
  );
};

export default DashboardPage;
